import { Euler, Object3D } from "three";
import { nextWall, prevWall } from "../../../util/SceneUtil";
import { Line } from "../../Line";
import { GeometryNodeExecutor } from "./GeometryNodeExecutor";
import { RailPart } from "./RailPart";
import { RailPartOptions, defaultRailPartOptions } from "./RailPartOptions";
import { createBox } from "./utils";

export interface BoxCreateOptions {
  width: number;
  height: number;
  depth: number;
  offsetX?: number;
  offsetY?: number;
  offsetZ?: number;
  rotation?: Euler;
}

export abstract class RailExecutor implements GeometryNodeExecutor {
  private _line?: Line;
  protected _railParts: RailPart[] = [];
  protected _railTop: Object3D = new Object3D();
  protected _options: RailPartOptions;
  protected _mesh?: Object3D;
  private _isExecuted: boolean = false;

  constructor(options: Partial<RailPartOptions> = {}) {
    this._options = { ...defaultRailPartOptions, ...options };
  }

  get parameters(): any {
    return this._options;
  }

  get line(): Line {
    return this._line!;
  }

  set line(value: Line) {
    this._line = value;
  }

  get isExecuted() {
    return this._isExecuted;
  }

  setAttribute(name: string, value: any) {
    (this as any)[name] = value;
  }

  getAttribute(name: string) {
    return (this as any)[name];
  }

  protected abstract createRailPart(width: number): RailPart;

  protected createTopMesh(width: number = 1.0) {
    return createBox({
      width: width,
      height: this._options.topHeight,
      depth: this._options.topDepth,
    });
  }

  protected calculatePartWidth(): {
    count: number;
    width: number;
    startDistance: number;
    endDistance: number;
  } {
    let endDistance = 0;
    const nWall = nextWall(this.line);
    if ((nWall as any)?._type === "Balcony") {
      endDistance = this._options.endOffset;
    }

    let startDistance = 0;
    const pWall = prevWall(this.line);
    if ((pWall as any)?._type === "Balcony") {
      startDistance = this._options.startOffset;
    }

    const count = Math.ceil(
      (this.line.length - (endDistance + startDistance)) / this._options.width
    );

    let style = this.getAttribute("Style") || 0;

    const width =
      style > 0
        ? Math.min(
            this._options.width,
            this.line.length - (endDistance + startDistance)
          )
        : (this.line.length - (endDistance + startDistance)) / count;

    return { count, width, startDistance, endDistance };
  }

  protected addRail(mesh: Object3D) {
    if (!this._line) return;

    const { count, width, startDistance } = this.calculatePartWidth();

    this._railParts?.forEach((r) => r.dispose());
    this._railParts = [];

    for (let i = 0; i < count; i++) {
      let _width = Math.min(width, this.line.length - i * width);
      const railPart = this.createRailPart(_width);
      railPart.position.setX(
        startDistance + -this.line.length / 2.0 + i * width
      );
      railPart.position.setZ(this._options.depthOffset);
      railPart.position.setY(this._options.heightOffset);
      this._railParts.push(railPart);
      mesh.add(railPart);
    }
  }

  protected addTop(mesh: Object3D) {
    if (!this._line) return;

    this._railTop = this.createTopMesh(this.line.length);
    this._railTop.position.set(
      -this.line.length / 2.0,
      this._options.height -
        this._options.topHeight +
        this._options.heightOffset,
      this._options.depthOffset
    );
    mesh.add(this._railTop);
  }

  execute(mesh: Object3D): void {
    if (!this._line) return;
    this._mesh = mesh;

    this.addRail(mesh);
    this.addTop(mesh);

    //bad code
    (this as any).baseWidth = this._line.length;
    (this as any).width = this._line.length;
    this._isExecuted = true;
  }

  dispose(): void {
    this._railParts?.forEach((r) => r.dispose());
    this._railTop.removeFromParent();
  }
}
